import React, { useState, useMemo } from "react";
import MessageUtils from "../utils/MessageUtils";
import Button from "./base/Button";
import Glyphicon from "./base/Glyphicon";
import styles from "./SubviewError.module.css";

/**
 * @typedef {import("@/stores/PreviewStore").PreviewError} PreviewError
 */

/**
 * 
 * @param {Object} props 
 * @param {string=} props.closeBtnLabel
 * todo: type remaining expected errors
 * @param {PreviewError=} props.error
 * @param {(() => void)=} props.onClose 
 * @param {boolean=} props.showCloseBtn
 * @param {string=} props.userMessage
 * @returns 
 */
const SubviewError = ({
  error,
  showCloseBtn = true,
  closeBtnLabel = "Close",
  userMessage = "An error occurred and we couldn't complete the request",
  onClose,
}) => {
  const [showDebugData, setShowDebugData] = useState(false);

  const { message, helpLink, debugInfo } = useMemo(() => {
    const msg = error?.message || "";
    const link = error?.helpLink;

    let debug = null;
    if (error?.debug || error?.data) {
      debug = getDebugInfo(error);
    }

    return {
      message: msg,
      helpLink: link,
      debugInfo: debug,
    };
  }, [error]);

  const handleClose = () => {
    onClose?.();
  };

  const toggleDebugData = () => {
    setShowDebugData((prev) => !prev);
  };

  return (
    <div className={styles.viewCentered}>
      <div className={styles.centeredContent}>
        <div className={styles.iconContainer}>
          <Glyphicon glyph="warning_sign" className={styles.icon} />
        </div>
        <div className={styles.errorMessage}>{MessageUtils.getErrorExpression()}!</div>
        <div className={styles.messageContainer}>
          <div className={styles.userMessage}>{userMessage}</div>
          {message.length > 0 && (
            <div className={styles.errorDetails}>
              {message}&nbsp;
              {helpLink && (
                <a href={helpLink} target="_blank" rel="noreferrer">
                  Learn More
                </a>
              )}
            </div>
          )}
        </div>
        {showCloseBtn && (
          <Button look="primary" onClick={handleClose}>
            {closeBtnLabel}
          </Button>
        )}
        {debugInfo && (
          <div className={styles.debugContainer}>
            <div className={styles.debugTitle} onClick={toggleDebugData}>
              More Info
              <span className={styles.debugToggle}>{showDebugData ? "▼" : "▶"}</span>
            </div>
            {showDebugData && (
              <div className={styles.debugContent}>
                {debugInfo.errorMessage && (
                  <>
                    {debugInfo.errorMessage}
                    {debugInfo.data && "\n\n"}
                  </>
                )}
                {debugInfo.data}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Normalizes error data for display
 * todo: type remaining errors
 * @param {import("@/stores/PreviewStore").PreviewError} error 
 * @returns {{ errorMessage?: string, data?: string } | null}
 */
const getDebugInfo = (error) => {
  const errorMessage = error?.debug || "";

  let data = "";
  if (error?.data) {
    try {
      data = JSON.stringify(error.data, null, 2);
    } catch (_) {
      data = "Error parsing error data";
    }
  }

  return errorMessage || data ? { errorMessage, data } : null;
};

export default SubviewError;
